import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { isSquaredDisplay } from 'components/content/models/ContentModule';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { getImageAspect, getImagePickerDescriptionByDisplay, MarketingMessageProps } from './base';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import {
    buildModuleSchedulingFormValues,
    defaultModuleSchedulingFormValues
} from '../module-scheduling/ModuleSchedulingForm';

interface IBrowserItemProperties {
    url: string;
    imageUrl: string;
    requiresLocationSelection?: boolean;
    title?: string;
    subtitle?: string;
    subtitleTwo?: string;
    invertTextColour?: boolean;
    showTextBackgroundBar?: boolean;
}

const validationSchema = Yup.object().shape({
    url: Yup.string().required('This field is required.').url('Invalid URL.'),
    name: Yup.string().required('This field is required.'),
    imageUrl: Yup.string().required('This field is required.').url('Invalid URL.')
});

export const BrowserMessageProperties: React.FC<MarketingMessageProps<IBrowserItemProperties>> = props => {
    const {
        display,
        properties = {} as IBrowserItemProperties,
        onSubmit,
        name,
        contextForm,
        context
    } = props;
    const hideMarketingMessageOverlay = isSquaredDisplay(display);

    const initialValues = React.useMemo(() => {
        const scheduleValues = context
            ? buildModuleSchedulingFormValues({
                  visibleTo: context.visibleTo,
                  visibleFrom: context.visibleFrom,
                  schedule: context.schedule
              })
            : null;
        return {
            name: name || '',
            url: properties.url || '',
            imageUrl: properties.imageUrl || '',
            title: properties.title || '',
            subtitle: properties.subtitle || '',
            subtitleTwo: properties.subtitleTwo || '',
            invertTextColour: properties.invertTextColour || false,
            showTextBackgroundBar: properties.showTextBackgroundBar || false,
            ...(scheduleValues ?? defaultModuleSchedulingFormValues)
        };
    }, [name, properties, context]);

    const handleSubmit = React.useCallback(
        (data: any) => {
            const request = Object.entries(data).reduce((acc: any, [field, item]) => {
                if (!!item || typeof item === 'boolean') {
                    acc[field] = item;
                }
                return acc;
            }, {});
            onSubmit(request);
        },
        [onSubmit]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, isSubmitting, isValid }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextFormField name="url" label={<MuiFormLabel required>URL</MuiFormLabel>} />
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="name"
                                label={<MuiFormLabel required>Reporting Name</MuiFormLabel>}
                                description="This code is used to track the number of marketing module item taps on anchor screen"
                            />
                        </Grid>
                        <Grid size={12}>
                            <ImageFormField
                                name="imageUrl"
                                label={<MuiFormLabel required>Image</MuiFormLabel>}
                                aspect={getImageAspect(display)}
                                accept={['image/jpeg', 'image/png', 'image/gif']}
                                description={getImagePickerDescriptionByDisplay(display)}
                            />
                        </Grid>
                        {!hideMarketingMessageOverlay && (
                            <>
                                <Grid size={12}>
                                    <Grid container direction="column">
                                        <Typography color="primary">Marketing message overlay</Typography>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="title" label="Title" />
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="subtitle" label="Subtitle" />
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField name="subtitleTwo" label="Subtitle two" />
                                </Grid>
                                <Grid size={12}>
                                    <SwitchFormField name="invertTextColour" label="Invert text colour" />
                                </Grid>
                                <Grid size={12}>
                                    <SwitchFormField
                                        name="showTextBackgroundBar"
                                        label="Show text background bar"
                                    />
                                </Grid>
                            </>
                        )}
                        {contextForm()}
                        <Grid size={12}>
                            <Row gutter align="flex-end" valign="center">
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting || !isValid}
                                    loading={isSubmitting}
                                    onClick={submitForm}
                                >
                                    Submit
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
